import { useQuery } from "@apollo/client";
import _ from "lodash";
import { DateTime } from "luxon";
import { StringParam, useQueryParam, withDefault } from "use-query-params";

import { Container, makeStyles } from "@material-ui/core";

import ErrorCard from "src/components/ErrorCard";
import LoadingIndicator from "src/components/LoadingIndicator";
import { useTenant } from "src/components/LoggedInAdminProvider";
import PageTitle from "src/components/PageTitle";
import UsageBreakdownTable from "src/components/usage/UsageBreakdownTable";
import UsageDateRangePicker from "src/components/usage/UsageDateRangePicker";
import UsageTimeline from "src/components/usage/UsageTimeline";
import {
  usageOfTenant,
  usageOfTenantVariables
} from "src/graphql/generated/usageOfTenant";
import { USAGE_OF_TENANT } from "src/graphql/usage";

const useStyles = makeStyles(theme => ({
  tableHeader: {
    marginTop: theme.spacing(6)
  },
  headerRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between"
  },
  dateRangeWrapper: {
    paddingTop: theme.spacing(3)
  }
}));

function UsageData({
  startDate,
  endDate
}: {
  startDate: string;
  endDate: string;
}) {
  const tenant = useTenant()!;
  const classes = useStyles();
  const { loading, error, data } = useQuery<
    usageOfTenant,
    usageOfTenantVariables
  >(USAGE_OF_TENANT, {
    variables: {
      startDate,
      endDate
    }
  });

  if (loading) {
    return <LoadingIndicator />;
  }

  if (error) {
    return <ErrorCard errors={[error]} />;
  }

  return (
    <>
      <UsageTimeline
        startDate={startDate}
        endDate={endDate}
        data={data!.usageOfTenantByDay}
      />
      <h1 className={classes.tableHeader}>Subaccounts</h1>
      <UsageBreakdownTable
        nameTitle="Subaccount"
        rows={data!.usageOfTenantBySubaccount.map(row => ({
          calls: row.calls,
          voicemails: row.voicemails,
          id: row.subaccount.id,
          name: row.subaccount.name
        }))}
        searchTitle="Find a subaccount..."
        linkToRow={row =>
          `/subaccounts/${row.id}/usage?start_date=${startDate}&end_date=${endDate}`
        }
        csvExportFileName={`${tenant.name}_usage_by_subaccount_${startDate}_${endDate}.csv`}
      />
    </>
  );
}

function validateStartDate(startDate: string): string {
  const dt = DateTime.fromISO(startDate);
  if (!dt.isValid) {
    return DateTime.now().startOf("month").toISODate();
  }
  return dt.toISODate();
}

function validateEndDate(endDate: string, validatedStartDate: string): string {
  const dt = DateTime.fromISO(endDate);
  if (!dt.isValid || dt < DateTime.fromISO(validatedStartDate)) {
    return DateTime.now().endOf("month").toISODate();
  }
  return dt.toISODate();
}

export default function OrgUsage(): JSX.Element {
  const classes = useStyles();

  const [startDate, setStartDate] = useQueryParam(
    "start_date",
    withDefault(StringParam, DateTime.now().startOf("month").toISODate())
  );
  const [endDate, setEndDate] = useQueryParam(
    "end_date",
    withDefault(StringParam, DateTime.now().endOf("month").toISODate())
  );

  const validatedStartDate = validateStartDate(startDate);
  const validatedEndDate = validateEndDate(endDate, validatedStartDate);

  return (
    <Container>
      <div className={classes.headerRow}>
        <PageTitle title="Usage" />
        <div className={classes.dateRangeWrapper}>
          <UsageDateRangePicker
            startDate={validatedStartDate}
            endDate={validatedEndDate}
            onChange={(newStartDate, newEndDate) => {
              setStartDate(newStartDate);
              setEndDate(newEndDate);
            }}
          />
        </div>
      </div>
      <UsageData startDate={startDate} endDate={endDate} />
    </Container>
  );
}
